import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  Badge,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import { getRoleLabel, getRoleColor } from '../services/mockUsers';
import { useNavigate } from 'react-router-dom';

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderRole?: string;
  content: string;
  timestamp: string;
  type: 'text' | 'file' | 'image';
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  isRead: boolean;
  isDelivered: boolean;
}

interface ChatRoom {
  id: string;
  name: string;
  type: 'direct' | 'group' | 'project';
  participants: string[];
  projectId?: string;
  lastMessage?: ChatMessage;
  unreadCount: number;
  avatar?: string;
}

const Chat: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0); // 0: Projets, 1: Autres conversations
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadRooms();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { users: allUsers } = await api.users.getAll();
      setUsers(allUsers);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  useEffect(() => {
    if (selectedRoom) {
      loadMessages(selectedRoom.id);
      // Marquer comme lu
      const updatedRooms = rooms.map(room =>
        room.id === selectedRoom.id ? { ...room, unreadCount: 0 } : room
      );
      setRooms(updatedRooms);
    }
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadRooms = async () => {
    try {
      // Charger les projets depuis Supabase
      const { projects } = await api.projects.getAll();
      
      // Les projets sont déjà filtrés par l'API selon le rôle
      // Les agents terrain ne voient que leurs projets assignés
      // Les admins, agences et SOBEBRA voient tous les projets
      const userProjects = projects;

    // Messages de démonstration avec compteurs de non-lus
    const projectUnreadCounts: Record<string, number> = {
      'project-1': 3, // 3 messages non lus
      'project-2': 2, // 2 messages non lus
      'project-3': 1, // 1 message non lu
    };

    const projectRooms: ChatRoom[] = userProjects.map(project => {
      const roomId = `project-${project.id}`;
      const lastMessages: Record<string, ChatMessage> = {
        'project-1': {
          id: 'msg-p1-8',
          senderId: 'admin-001',
          senderName: 'Administrateur Principal',
          senderRole: 'admin',
          content: 'Merci pour le rapport détaillé. Le projet avance bien. Continuez ainsi !',
          timestamp: '2024-11-15T16:30:00Z',
          type: 'text',
          isRead: false,
          isDelivered: true,
        },
        'project-2': {
          id: 'msg-p2-6',
          senderId: 'soebra-001',
          senderName: 'SOBEBRA - Direction Marketing',
          senderRole: 'soebra',
          content: 'Les résultats préliminaires sont très encourageants. Nous attendons le rapport final avec impatience.',
          timestamp: '2024-11-15T10:00:00Z',
          type: 'text',
          isRead: false,
          isDelivered: true,
        },
        'project-3': {
          id: 'msg-p3-7',
          senderId: 'agence-001',
          senderName: 'Agence Zenith Productions',
          senderRole: 'agence',
          content: 'Rapport de suivi quotidien envoyé. Tout est conforme aux attentes.',
          timestamp: '2024-11-15T08:00:00Z',
          type: 'text',
          isRead: false,
          isDelivered: true,
        },
      };

      return {
        id: roomId,
        name: project.title,
        type: 'project' as const,
        participants: [
          project.created_by,
          project.manager_agence,
          project.contact_soebra,
          project.assigned_to,
        ].filter(Boolean) as string[],
        projectId: project.id,
        unreadCount: projectUnreadCounts[roomId] || 0,
        lastMessage: lastMessages[roomId],
      };
    });

    // Autres conversations (directes et groupes)
    const otherRooms: ChatRoom[] = [
      {
        id: 'room-1',
        name: 'Koffi Amoussou',
        type: 'direct',
        participants: ['admin-001', 'agent-001'],
        unreadCount: 2,
      },
      {
        id: 'room-2',
        name: 'Équipe Cotonou Centre',
        type: 'group',
        participants: ['admin-001', 'agent-001', 'agent-002', 'agent-003'],
        unreadCount: 0,
      },
    ];

    setRooms([...projectRooms, ...otherRooms]);
    } catch (error) {
      console.error('Erreur lors du chargement des salles de chat:', error);
      // En cas d'erreur, utiliser des salles vides
      setRooms([]);
    }
  };

  const loadMessages = async (roomId: string) => {
    try {
      console.log('Chargement des messages pour la room:', roomId);
      
      // Charger tous les messages de l'utilisateur depuis la base de données
      const { messages: dbMessages } = await api.messages.getAll();
      console.log('Messages récupérés de la DB:', dbMessages.length);
      
      let filteredMessages: any[] = [];
      
      // Si c'est un projet, charger les messages depuis la base de données
      if (roomId.startsWith('project-')) {
        const projectId = roomId.replace('project-', '');
        console.log('Filtrage des messages pour le projet:', projectId);
        
        // Filtrer les messages pour ce projet
        filteredMessages = dbMessages.filter((msg: any) => {
          // Le message doit être lié à ce projet ET l'utilisateur doit être soit sender soit recipient
          return msg.project_id === projectId && 
                 (msg.sender_id === user?.id || msg.recipient_id === user?.id);
        });
        
        console.log('Messages filtrés pour le projet:', filteredMessages.length);
      } else {
        // Pour les conversations directes, charger depuis la base de données
        // Filtrer les messages pour cette conversation (basé sur le destinataire)
        filteredMessages = dbMessages.filter((msg: any) => {
          // Si c'est une conversation directe, vérifier que l'utilisateur est soit sender soit recipient
          // ET que le message n'est pas lié à un projet (pour les conversations directes)
          return (msg.sender_id === user?.id || msg.recipient_id === user?.id) &&
                 !msg.project_id;
        });
        
        console.log('Messages filtrés pour conversation directe:', filteredMessages.length);
      }
      
      // Transformer en format ChatMessage et trier par date
      const transformedMessages: ChatMessage[] = filteredMessages
        .map((msg: any) => ({
          id: msg.id,
          senderId: msg.sender_id,
          senderName: msg.sender_name || 'Utilisateur',
          senderRole: undefined, // À récupérer depuis users si nécessaire
          content: msg.content || msg.subject || '',
          timestamp: msg.created_at,
          type: 'text' as const,
          isRead: msg.is_read || false,
          isDelivered: true,
        }))
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      console.log('Messages transformés et triés:', transformedMessages.length);
      setMessages(transformedMessages);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
      setMessages([]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom || !user) return;

    try {
      // Déterminer le destinataire selon le type de room
      let recipientId: string | undefined;
      if (selectedRoom.type === 'direct') {
        // Pour une conversation directe, trouver le destinataire
        recipientId = selectedRoom.participants.find(p => p !== user.id);
      } else if (selectedRoom.type === 'project' && selectedRoom.projectId) {
        // Pour un projet, envoyer à tous les participants du projet
        // Pour l'instant, on envoie au premier participant (à améliorer)
        recipientId = selectedRoom.participants.find(p => p !== user.id);
      }

      if (!recipientId && selectedRoom.type === 'project' && selectedRoom.projectId) {
        // Si c'est un projet, on peut créer un message sans destinataire spécifique
        // mais avec un project_id
        const projectId = selectedRoom.projectId;
        const messageContent = newMessage;
        
        // Sauvegarder le message dans la base de données
        const response = await api.messages.send({
          recipient_id: user.id, // Envoyer à soi-même pour l'instant (à améliorer)
          project_id: projectId,
          subject: `Message dans ${selectedRoom.name}`,
          content: messageContent,
        });

        console.log('Message envoyé avec succès:', response);
        
        // Ajouter le message localement immédiatement pour une meilleure UX
        if (response.messageData) {
          const newMsg: ChatMessage = {
            id: response.messageData.id,
            senderId: response.messageData.sender_id,
            senderName: user.name || 'Vous',
            senderRole: user.role,
            content: response.messageData.content || messageContent,
            timestamp: response.messageData.created_at || new Date().toISOString(),
            type: 'text' as const,
            isRead: false,
            isDelivered: true,
          };
          setMessages(prev => [...prev, newMsg]);
        }
        
        // Attendre un court délai pour que la base de données soit à jour
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Recharger les messages après l'envoi pour synchroniser
        await loadMessages(selectedRoom.id);
        setNewMessage('');
      } else if (recipientId) {
        const messageContent = newMessage;
        
        // Sauvegarder le message dans la base de données
        const response = await api.messages.send({
          recipient_id: recipientId,
          subject: `Message de ${user.name}`,
          content: messageContent,
        });

        console.log('Message envoyé avec succès:', response);
        
        // Ajouter le message localement immédiatement pour une meilleure UX
        if (response.messageData) {
          const newMsg: ChatMessage = {
            id: response.messageData.id,
            senderId: response.messageData.sender_id,
            senderName: user.name || 'Vous',
            senderRole: user.role,
            content: response.messageData.content || messageContent,
            timestamp: response.messageData.created_at || new Date().toISOString(),
            type: 'text' as const,
            isRead: false,
            isDelivered: true,
          };
          setMessages(prev => [...prev, newMsg]);
        }
        
        // Attendre un court délai pour que la base de données soit à jour
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Recharger les messages après l'envoi pour synchroniser
        await loadMessages(selectedRoom.id);
        setNewMessage('');
      } else {
        // Fallback : ajouter localement si on ne peut pas déterminer le destinataire
        const message: ChatMessage = {
          id: 'msg-' + Date.now(),
          senderId: user.id,
          senderName: user.name || 'Vous',
          senderRole: user.role,
          content: newMessage,
          timestamp: new Date().toISOString(),
          type: 'text',
          isRead: false,
          isDelivered: false,
        };

        setMessages([...messages, message]);
        setNewMessage('');

        setTimeout(() => {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === message.id ? { ...msg, isDelivered: true } : msg
            )
          );
        }, 500);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi du message:', error);
      alert('Erreur lors de l\'envoi du message: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRoom) return;

    setUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      setUploadProgress(100);

      const isImage = file.type.startsWith('image/');
      const message: ChatMessage = {
        id: 'msg-' + Date.now(),
        senderId: user?.id || '',
        senderName: user?.name || 'Vous',
        senderRole: user?.role,
        content: isImage ? 'Photo envoyée' : 'Fichier envoyé',
        timestamp: new Date().toISOString(),
        type: isImage ? 'image' : 'file',
        fileName: file.name,
        fileUrl: URL.createObjectURL(file),
        fileSize: file.size,
        isRead: false,
        isDelivered: true,
      };

      setMessages([...messages, message]);
      setUploading(false);
      setUploadProgress(0);

      if (fileInput.current) {
        fileInput.current.value = '';
      }
    }, 2000);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (minutes < 1440) return `Il y a ${Math.floor(minutes / 60)} h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const isOwnMessage = (message: ChatMessage): boolean => {
    return message.senderId === user?.id;
  };

  const projectRooms = rooms.filter(r => r.type === 'project');
  const otherRooms = rooms.filter(r => r.type !== 'project');

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Liste des conversations */}
      <Paper
        sx={{
          width: 350,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight="bold">
            Conversations
          </Typography>
        </Box>

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Projets" icon={<FolderIcon />} iconPosition="start" />
          <Tab label="Autres" icon={<GroupIcon />} iconPosition="start" />
        </Tabs>

        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {tabValue === 0 ? (
            projectRooms.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="Aucun projet"
                  secondary="Vous n'êtes assigné à aucun projet pour le moment"
                />
              </ListItem>
            ) : (
              projectRooms.map((room) => (
                <ListItem
                  key={room.id}
                  button
                  selected={selectedRoom?.id === room.id}
                  onClick={() => setSelectedRoom(room)}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={room.unreadCount}
                      color="error"
                      invisible={room.unreadCount === 0}
                    >
                      <Avatar>
                        <FolderIcon />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight={room.unreadCount > 0 ? 'bold' : 'normal'}>
                          {room.name}
                        </Typography>
                        {room.lastMessage && (
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(room.lastMessage.timestamp)}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Chip label="Projet" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        {room.lastMessage && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mt: 0.5,
                            }}
                          >
                            {room.lastMessage.type === 'file' && <FileIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />}
                            {room.lastMessage.type === 'image' && <ImageIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />}
                            {room.lastMessage.content}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))
            )
          ) : (
            otherRooms.map((room) => (
              <ListItem
                key={room.id}
                button
                selected={selectedRoom?.id === room.id}
                onClick={() => setSelectedRoom(room)}
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={room.unreadCount}
                    color="error"
                    invisible={room.unreadCount === 0}
                  >
                    <Avatar>
                      {room.type === 'group' ? <GroupIcon /> : <PersonIcon />}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight={room.unreadCount > 0 ? 'bold' : 'normal'}>
                        {room.name}
                      </Typography>
                      {room.lastMessage && (
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(room.lastMessage.timestamp)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    room.lastMessage && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: room.unreadCount > 0 ? 'bold' : 'normal',
                        }}
                      >
                        {room.lastMessage.type === 'file' && <FileIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />}
                        {room.lastMessage.type === 'image' && <ImageIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />}
                        {room.lastMessage.content}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Paper>

      {/* Zone de chat */}
      {selectedRoom ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* En-tête */}
          <Paper sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar>
                  {selectedRoom.type === 'project' ? <FolderIcon /> : selectedRoom.type === 'group' ? <GroupIcon /> : <PersonIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedRoom.name}</Typography>
                  {selectedRoom.type === 'project' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip label="Groupe Projet" size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {selectedRoom.participants.length} participants
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => selectedRoom.projectId && navigate(`/projects/${selectedRoom.projectId}`)}
                      >
                        Voir le projet
                      </Button>
                    </Box>
                  )}
                  {selectedRoom.type === 'group' && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedRoom.participants.length} participants
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              bgcolor: '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {messages.map((message, index) => {
              const own = isOwnMessage(message);
              const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;
              const senderUser = users.find((u: any) => u.id === message.senderId);

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: own ? 'flex-end' : 'flex-start',
                    gap: 1,
                    alignItems: 'flex-end',
                  }}
                >
                  {!own && showAvatar && (
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {message.senderName.charAt(0)}
                    </Avatar>
                  )}
                  {!own && !showAvatar && <Box sx={{ width: 32 }} />}

                  <Box
                    sx={{
                      maxWidth: '70%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: own ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {!own && showAvatar && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, px: 1 }}>
                        <Typography variant="caption" fontWeight="bold">
                          {message.senderName}
                        </Typography>
                        {message.senderRole && (
                          <Chip
                            label={getRoleLabel(message.senderRole)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: getRoleColor(message.senderRole),
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                    )}

                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        bgcolor: own ? '#1976d2' : 'white',
                        color: own ? 'white' : 'text.primary',
                        borderRadius: 2,
                        borderTopLeftRadius: own ? 2 : 0,
                        borderTopRightRadius: own ? 0 : 2,
                      }}
                    >
                      {message.type === 'text' && (
                        <Typography variant="body1">{message.content}</Typography>
                      )}

                      {message.type === 'image' && (
                        <Box>
                          <Box
                            component="img"
                            src={message.fileUrl}
                            alt={message.fileName}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: 300,
                              borderRadius: 1,
                              mb: 1,
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZTwvdGV4dD48L3N2Zz4=';
                            }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                            {message.fileName}
                          </Typography>
                        </Box>
                      )}

                      {message.type === 'file' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FileIcon sx={{ fontSize: 32 }} />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {message.fileName}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {formatFileSize(message.fileSize || 0)}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          {formatTime(message.timestamp)}
                        </Typography>
                        {own && (
                          <Box>
                            {message.isDelivered ? (
                              message.isRead ? (
                                <CheckCircleIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              ) : (
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                              )
                            ) : (
                              <CheckCircleOutlineIcon sx={{ fontSize: 16, opacity: 0.3 }} />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>

          {uploading && (
            <Box sx={{ px: 2, py: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Upload en cours... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {/* Zone de saisie */}
          <Paper sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <input
                ref={fileInput}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              />
              <Tooltip title="Joindre un fichier">
                <IconButton color="primary" onClick={() => fileInput.current?.click()} disabled={uploading}>
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Tapez votre message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={uploading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || uploading}
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" color="text.secondary">
            Sélectionnez une conversation pour commencer
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Chat;

